import * as R from 'remeda';
import { Url } from '@rossum/api-client';
import {
  DatapointData,
  MultivalueData,
  SectionData,
} from '@rossum/api-client/annotations';
import { SchemaSection } from '@rossum/api-client/schemas';
import { flattenDatapoints } from '../flattenDatapoints';

export type ContentForPreview =
  | {
      type: 'from_annotation';
      annotationUrl: Url;
      annotationQueue: Url;
    }
  | {
      type: 'from_dependencies';
      queue: Url;
      schemaContent: SchemaSection[];
      dependencies: Record<string, string>;
    };

// When the dependency is inside a multivalue (schema-wise), create a tuple with a single row and put the datapoints inside of it
const makeMultivalue = (
  multivalueSchemaId: string,
  datapoints: DatapointData[]
): MultivalueData => {
  return {
    id: 1,
    url: '',
    children: [
      {
        id: 1,
        url: '',
        category: 'tuple',
        children: datapoints,
        schemaId: 'artificial_tuple',
      },
    ],
    category: 'multivalue',
    schemaId: multivalueSchemaId,
  };
};

const makeDatapoint = ({
  schemaId,
  value,
  struct,
}: {
  schemaId: string;
  value: string;
  struct: unknown;
}): DatapointData => ({
  id: 1,
  url: '',
  content: {
    value,
    normalizedValue: value,
    struct,
  },
  category: 'datapoint',
  schemaId,
  validationSources: [],
  noRecalculation: true,
});

export const makeArtificialContent = (
  dependencies: Record<string, string>,
  schemaContent: SchemaSection[]
): Array<SectionData> => {
  const flatDatapoints = flattenDatapoints(schemaContent);

  const dependenciesWithParents = Object.entries(dependencies).map(
    ([schemaId, value]) => {
      const schemaDatapoint = flatDatapoints.find(dp => dp.id === schemaId);

      const parentMultivalueSchemaId =
        schemaDatapoint?.parentMultivalueSchemaId;

      const struct =
        schemaDatapoint?.type === 'enum'
          ? schemaDatapoint.options.find(option => option.value === value)
              ?.struct
          : null;

      return {
        schemaId,
        parentMultivalueSchemaId,
        value,
        struct,
      };
    }
  );

  const [headerFields, lineItems] = R.partition(
    dependenciesWithParents,
    dep => dep.parentMultivalueSchemaId === undefined
  );

  const dependenciesByMultivalue = R.groupBy(
    lineItems,
    item => item.parentMultivalueSchemaId
  );

  return [
    {
      id: 1,
      url: '',
      children: [
        ...headerFields.map(makeDatapoint),
        ...Object.entries(dependenciesByMultivalue).map(
          ([multivalueSchemaId, datapoints]) =>
            makeMultivalue(multivalueSchemaId, datapoints.map(makeDatapoint))
        ),
      ],
      category: 'section',
      schemaId: 'artificial_section',
    },
  ];
};
